import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { Button } from "./ui/Button";

// Supervisor side of the account-deletion request flow. Lists every
// pending request in the agency plus the most recent resolved ones
// for context. Approve runs the existing soft-archive deleteUser
// (flips profiles.disabled_at + scrubs the auth session); deny
// requires a note that's sent back to the officer.
//
// Visible to anyone L2+ via the RLS policy on deletion_requests
// (level_rank <= 2 — owner, l1, l2). The card hides itself when
// there are no requests so it doesn't add clutter to Settings for
// supervisors who have nothing pending.

export function AccountDeletionRequestsPanel() {
  const user = useEffectiveUser();
  const canManage = !!user && (user.level === "owner" || user.level === "l1" || user.level === "l2");
  const qc = useQueryClient();

  const requestsQ = useQuery({
    queryKey: ["deletion-requests"],
    queryFn: api.listDeletionRequests,
    enabled: canManage,
  });
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: api.listUsers,
    enabled: canManage,
  });

  const userById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersQ.data ?? []) {
      map.set(u.id, u.username || u.email || u.id);
    }
    return map;
  }, [usersQ.data]);

  const requests = requestsQ.data ?? [];
  const pending = requests.filter((r) => r.status === "pending");
  const resolved = requests
    .filter((r) => r.status !== "pending")
    .slice(0, 5);

  if (!canManage) return null;
  if (pending.length === 0 && resolved.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">
          Account deletion requests
          {pending.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold">
              {pending.length}
            </span>
          )}
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Officers submit deletion requests here when they want their
          account removed. Approve to soft-archive the account
          (schedule history is preserved). Deny if it's a mistake or
          contrary to policy — the officer is notified either way.
        </p>
      </div>

      {pending.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
          {pending.map((r) => (
            <RequestRow
              key={r.id}
              id={r.id}
              who={userById.get(r.userId) ?? r.userId}
              requestedAt={r.requestedAt}
              reason={r.reason}
              onChange={() => qc.invalidateQueries({ queryKey: ["deletion-requests"] })}
            />
          ))}
        </ul>
      )}

      {resolved.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer select-none hover:text-slate-700">
            Recent resolved ({resolved.length})
          </summary>
          <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
            {resolved.map((r) => (
              <li key={r.id} className="px-3 py-2 bg-slate-50/60">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-700">
                    {userById.get(r.userId) ?? r.userId}
                  </span>
                  <span
                    className={
                      "text-[10px] uppercase tracking-wider font-semibold "
                      + (r.status === "approved" ? "text-emerald-700"
                        : r.status === "denied" ? "text-red-700"
                        : "text-slate-500")
                    }
                  >
                    {r.status}
                  </span>
                </div>
                {r.resolvedAt && (
                  <div className="text-[11px] text-slate-500">
                    {new Date(r.resolvedAt).toLocaleString()}
                  </div>
                )}
                {r.resolvedNote && (
                  <div className="mt-1 text-[11px] italic">
                    Note: {r.resolvedNote}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function RequestRow({
  id, who, requestedAt, reason, onChange,
}: {
  id: string;
  who: string;
  requestedAt: string;
  reason: string | null;
  onChange: () => void;
}) {
  const [denying, setDenying] = useState(false);
  const [denyNote, setDenyNote] = useState("");
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approveMut = useMutation({
    mutationFn: () => api.approveDeletionRequest(id),
    onSuccess: () => { setApproving(false); onChange(); },
    onError: (e: Error) => setError(e.message),
  });
  const denyMut = useMutation({
    mutationFn: () => api.denyDeletionRequest(id, denyNote),
    onSuccess: () => { setDenying(false); setDenyNote(""); onChange(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <li className="px-3 py-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">{who}</div>
          <div className="text-[11px] text-slate-500">
            requested {new Date(requestedAt).toLocaleString()}
          </div>
          {reason && (
            <div className="mt-1 text-xs italic text-slate-600">
              "{reason}"
            </div>
          )}
        </div>
        {!denying && !approving && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="secondary" onClick={() => setDenying(true)}>
              Deny
            </Button>
            <Button size="sm" variant="danger" onClick={() => setApproving(true)}>
              Approve
            </Button>
          </div>
        )}
      </div>

      {approving && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 space-y-2">
          <p>
            <strong>Approve and delete {who}'s account?</strong> They'll
            be signed out and their profile disabled. Schedule history
            stays attached to the agency.
          </p>
          {error && <div>{error}</div>}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={() => { setError(null); approveMut.mutate(); }}
              disabled={approveMut.isPending}
            >
              {approveMut.isPending ? "Approving…" : "Confirm approve"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setApproving(false); setError(null); }}
              disabled={approveMut.isPending}
            >
              Back
            </Button>
          </div>
        </div>
      )}

      {denying && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-2">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Reason (sent to the officer)
            </span>
            <textarea
              value={denyNote}
              onChange={(e) => setDenyNote(e.target.value)}
              placeholder="e.g. Agency retention policy requires the profile to remain on file for the duration of employment."
              rows={3}
              className="w-full text-xs rounded border border-slate-300 bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-400"
            />
          </label>
          {error && <div className="text-red-700">{error}</div>}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setError(null); denyMut.mutate(); }}
              disabled={denyMut.isPending || !denyNote.trim()}
            >
              {denyMut.isPending ? "Sending…" : "Confirm deny"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setDenying(false); setDenyNote(""); setError(null); }}
              disabled={denyMut.isPending}
            >
              Back
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
